/**
 * Generic, pricing-agnostic numerical root-finders.
 *
 * Each finder solves `f(x) = 0` and reports not just the root but how many
 * function evaluations it took and whether it actually converged. That metadata
 * is what lets the IV layer (and its tests) compare methods apples-to-apples —
 * e.g. proving Brent reaches the same root as bisection in far fewer iterations.
 *
 * These functions know nothing about options. Inject `f` (and, for Newton, its
 * derivative) and they return a root. See `./solvers` for the IV-specific layer
 * that wires Black-Scholes pricing into these.
 */

export interface RootFinderResult {
  /** Best estimate of the root. `NaN` when the problem was ill-posed (e.g. not bracketed). */
  root: number;
  /** Number of times `f` was evaluated inside the iteration loop. */
  iterations: number;
  /** True when a stopping criterion was met before `maxIterations`. */
  converged: boolean;
}

export interface BracketingOptions {
  /** Stop when the residual `|f(x)|` drops below this. Defaults per finder. */
  fTolerance?: number;
  /** Stop when the bracket width (or step) drops below this. Defaults per finder. */
  xTolerance?: number;
  /** Hard cap on iterations. */
  maxIterations?: number;
}

export interface NewtonOptions {
  fTolerance?: number;
  maxIterations?: number;
  /** Lower clamp applied to every iterate (keeps Newton from escaping a sane domain). */
  lower?: number;
  /** Upper clamp applied to every iterate. */
  upper?: number;
}

const DEFAULT_F_TOL = 1e-6;
const DEFAULT_X_TOL = 1e-8;
const DEFAULT_MAX_ITERATIONS = 100;

/**
 * Bisection — bracket the root in `[lower, upper]` (requires a sign change) and
 * halve the interval each step. Linear convergence (rate ½): bulletproof but
 * slow. Works for any continuous, monotone-in-sign `f`; the direction is derived
 * from the sign of `f`, so it does not assume `f` is increasing.
 */
export function bisection(
  f: (x: number) => number,
  lower: number,
  upper: number,
  options: BracketingOptions = {},
): RootFinderResult {
  const {
    fTolerance = DEFAULT_F_TOL,
    xTolerance = DEFAULT_X_TOL,
    maxIterations = DEFAULT_MAX_ITERATIONS,
  } = options;

  let lo = lower;
  let hi = upper;
  let flo = f(lo);
  const fhi = f(hi);

  if (flo === 0) return { root: lo, iterations: 0, converged: true };
  if (fhi === 0) return { root: hi, iterations: 0, converged: true };
  // No sign change → root is not inside the bracket. Bail loudly (NaN) rather
  // than silently returning a meaningless midpoint.
  if (flo * fhi > 0) return { root: NaN, iterations: 0, converged: false };

  let mid = (lo + hi) / 2;
  let i = 0;
  for (; i < maxIterations; i++) {
    mid = (lo + hi) / 2;
    const fmid = f(mid);

    if (Math.abs(fmid) < fTolerance || (hi - lo) / 2 < xTolerance) {
      return { root: mid, iterations: i + 1, converged: true };
    }

    // Keep whichever half still straddles the root.
    if (Math.sign(fmid) === Math.sign(flo)) {
      lo = mid;
      flo = fmid;
    } else {
      hi = mid;
    }
  }

  return { root: mid, iterations: i, converged: false };
}

/**
 * Newton-Raphson with bound clamping. Quadratic convergence when it behaves,
 * but it needs the derivative `fPrime` and can stall where `fPrime ≈ 0` (for
 * options: deep ITM/OTM, where vega vanishes). Clamping to `[lower, upper]`
 * keeps a bad step from launching the iterate into a nonsensical domain
 * (e.g. negative volatility), but Newton offers no convergence guarantee —
 * use Brent when robustness matters.
 */
export function newton(
  f: (x: number) => number,
  fPrime: (x: number) => number,
  guess: number,
  options: NewtonOptions = {},
): RootFinderResult {
  const {
    fTolerance = DEFAULT_F_TOL,
    maxIterations = DEFAULT_MAX_ITERATIONS,
    lower = -Infinity,
    upper = Infinity,
  } = options;

  let x = guess;
  let i = 0;
  for (; i < maxIterations; i++) {
    const fx = f(x);
    if (Math.abs(fx) < fTolerance) return { root: x, iterations: i + 1, converged: true };

    const dfx = fPrime(x);
    if (dfx === 0 || !Number.isFinite(dfx)) {
      // Flat (or undefined) derivative — Newton has nothing to step on.
      return { root: x, iterations: i + 1, converged: false };
    }

    let next = x - fx / dfx;
    if (next < lower) next = lower;
    else if (next > upper) next = upper;

    // Clamped against a wall and unable to improve → give up rather than spin.
    if (next === x) return { root: x, iterations: i + 1, converged: false };
    x = next;
  }

  return { root: x, iterations: i, converged: false };
}

/**
 * Brent-Dekker — a hybrid that keeps a bracket `[a, b]` (guaranteed convergence
 * like bisection) while trying inverse-quadratic interpolation / secant steps
 * for superlinear speed, falling back to bisection whenever a step fails to make
 * sufficient progress. Best of both: ~bisection's safety, ~secant's speed.
 *
 * Reference: Brent, "Algorithms for Minimization without Derivatives" (1973).
 */
export function brent(
  f: (x: number) => number,
  lower: number,
  upper: number,
  options: BracketingOptions = {},
): RootFinderResult {
  const {
    fTolerance = DEFAULT_X_TOL,
    xTolerance = DEFAULT_X_TOL,
    maxIterations = DEFAULT_MAX_ITERATIONS,
  } = options;

  let a = lower;
  let b = upper;
  let fa = f(a);
  let fb = f(b);

  if (fa === 0) return { root: a, iterations: 0, converged: true };
  if (fb === 0) return { root: b, iterations: 0, converged: true };
  if (fa * fb > 0) return { root: NaN, iterations: 0, converged: false };

  // Ensure b is the better estimate (smaller residual).
  if (Math.abs(fa) < Math.abs(fb)) {
    [a, b] = [b, a];
    [fa, fb] = [fb, fa];
  }

  let c = a;
  let fc = fa;
  let d = b - a; // step from two iterations ago (used by the progress tests)
  let usedBisection = true;
  let i = 0;

  for (; i < maxIterations; i++) {
    if (Math.abs(fb) < fTolerance || Math.abs(b - a) < xTolerance) {
      return { root: b, iterations: i + 1, converged: true };
    }

    let s: number;
    if (fa !== fc && fb !== fc) {
      // Inverse quadratic interpolation.
      s =
        (a * fb * fc) / ((fa - fb) * (fa - fc)) +
        (b * fa * fc) / ((fb - fa) * (fb - fc)) +
        (c * fa * fb) / ((fc - fa) * (fc - fb));
    } else {
      // Secant.
      s = b - (fb * (b - a)) / (fb - fa);
    }

    // Reject the interpolated step (and bisect instead) unless it lands in the
    // top quarter of the bracket AND is shrinking the step fast enough.
    const bound = (3 * a + b) / 4;
    const reject =
      !(s > Math.min(bound, b) && s < Math.max(bound, b)) ||
      (usedBisection && Math.abs(s - b) >= Math.abs(b - c) / 2) ||
      (!usedBisection && Math.abs(s - b) >= Math.abs(c - d) / 2) ||
      (usedBisection && Math.abs(b - c) < xTolerance) ||
      (!usedBisection && Math.abs(c - d) < xTolerance);

    if (reject) {
      s = (a + b) / 2;
      usedBisection = true;
    } else {
      usedBisection = false;
    }

    const fs = f(s);
    d = c;
    c = b;
    fc = fb;

    if (fa * fs < 0) {
      b = s;
      fb = fs;
    } else {
      a = s;
      fa = fs;
    }

    if (Math.abs(fa) < Math.abs(fb)) {
      [a, b] = [b, a];
      [fa, fb] = [fb, fa];
    }
  }

  return { root: b, iterations: i, converged: false };
}
